import { useEffect, useState } from "react";
import {
    BookOpen,
    Plus,
    Trash2,
    FileText,
    Download,
    Compass,
    Target,
    AlertTriangle,
    GitBranch,
    Network,
    ScrollText,
    Pencil,
    X,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

const EMPTY = {
    mission_objective: "",
    main_challenge: "",
    attention_points: [],
    decisions: [],
    network: [],
    synthesis: "",
};

// ─────────────────────────────────────────────────────────────
// StrategicReadingPanel — full editor for the lecture stratégique
// ─────────────────────────────────────────────────────────────

export function StrategicReadingPanel({ dossier }) {
    const dossierId = dossier.id;
    const [state, setState] = useState(EMPTY);
    const [options, setOptions] = useState([]);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [letter, setLetter] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [newPoint, setNewPoint] = useState("");
    const [newDecision, setNewDecision] = useState("");

    useEffect(() => {
        cockpit
            .get(`/dossiers/${dossierId}/strategic-reading`)
            .then(({ data }) => setState({ ...EMPTY, ...data }))
            .catch(() => setState(EMPTY));
        cockpit
            .get("/strategic-network-options")
            .then(({ data }) => setOptions(data.options || []))
            .catch(() => setOptions([]));
    }, [dossierId]);

    const save = async (overrides = {}) => {
        setBusy(true);
        setMsg("");
        try {
            const payload = { ...state, ...overrides };
            const { data } = await cockpit.put(
                `/dossiers/${dossierId}/strategic-reading`,
                payload,
            );
            setState({ ...EMPTY, ...data });
            setMsg("Lecture stratégique enregistrée ✓");
            setTimeout(() => setMsg(""), 2500);
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const addPoint = () => {
        const v = newPoint.trim();
        if (!v) return;
        const next = [...(state.attention_points || []), v];
        setState({ ...state, attention_points: next });
        setNewPoint("");
        save({ attention_points: next });
    };

    const removePoint = (idx) => {
        const next = (state.attention_points || []).filter((_, i) => i !== idx);
        setState({ ...state, attention_points: next });
        save({ attention_points: next });
    };

    const addDecision = () => {
        const v = newDecision.trim();
        if (!v) return;
        const next = [
            ...(state.decisions || []),
            { label: v, decided_at: new Date().toISOString() },
        ];
        setState({ ...state, decisions: next });
        setNewDecision("");
        save({ decisions: next });
    };

    const removeDecision = (id) => {
        const next = (state.decisions || []).filter((d) => d.id !== id);
        setState({ ...state, decisions: next });
        save({ decisions: next });
    };

    const toggleNetwork = (opt) => {
        const cur = state.network || [];
        const next = cur.includes(opt)
            ? cur.filter((v) => v !== opt)
            : [...cur, opt];
        setState({ ...state, network: next });
        save({ network: next });
    };

    const generateLetter = async () => {
        setGenerating(true);
        try {
            const { data } = await cockpit.post(
                `/dossiers/${dossierId}/mission-letter`,
            );
            setLetter(data);
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Génération impossible");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <section
            data-testid="cockpit-strategic-reading"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <BookOpen
                            strokeWidth={1}
                            size={14}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Cockpit de décision stratégique
                    </div>
                    <h2
                        className="font-serif text-[26px] sm:text-[30px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Lecture stratégique
                    </h2>
                    <p className="mt-2 text-[12.5px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                        Mémoire interne du dossier — visible uniquement côté
                        Cockpit. Cette zone matérialise la valeur du conseil
                        et sert de socle à la lettre de mission.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="generate-mission-letter"
                    onClick={generateLetter}
                    disabled={generating}
                    className="inline-flex items-center gap-3 px-5 py-3 text-[10px] tracking-[0.3em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    <FileText strokeWidth={1.2} size={13} />
                    {generating ? "Génération…" : "Générer la lettre de mission"}
                </button>
            </div>

            {msg && (
                <div
                    data-testid="strategic-msg"
                    className="mb-6 text-[12px] text-[#d4af37]/85 border-l-2 border-[#d4af37] pl-3 py-1 italic"
                >
                    {msg}
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7 space-y-10">
                    {/* Objectif de mission */}
                    <Block
                        label="Objectif de mission"
                        sub="Phrase synthétique sur le rôle confié à XV Capital Partners."
                        Icon={Target}
                    >
                        <textarea
                            data-testid="strategic-mission-objective"
                            value={state.mission_objective}
                            onChange={(e) =>
                                setState({ ...state, mission_objective: e.target.value })
                            }
                            onBlur={() => save()}
                            rows={3}
                            placeholder="Accompagner le dirigeant dans la structuration de son développement international tout en préservant le contrôle de l'actionnariat."
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-4 text-[14.5px] leading-[1.8] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 resize-y"
                        />
                    </Block>

                    {/* Enjeu principal */}
                    <Block
                        label="Enjeu principal"
                        sub="Décision stratégique recherchée pour le dossier."
                        Icon={Compass}
                    >
                        <textarea
                            data-testid="strategic-main-challenge"
                            value={state.main_challenge}
                            onChange={(e) =>
                                setState({ ...state, main_challenge: e.target.value })
                            }
                            onBlur={() => save()}
                            rows={3}
                            placeholder="Identifier un partenaire stratégique de long terme sans dilution excessive."
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-4 text-[14.5px] leading-[1.8] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 resize-y"
                        />
                    </Block>

                    {/* Synthèse stratégique */}
                    <Block
                        label="Synthèse stratégique XV"
                        sub="Analyse interne libre — peut s'étendre sur plusieurs paragraphes."
                        Icon={ScrollText}
                    >
                        <textarea
                            data-testid="strategic-synthesis"
                            value={state.synthesis}
                            onChange={(e) =>
                                setState({ ...state, synthesis: e.target.value })
                            }
                            onBlur={() => save()}
                            rows={9}
                            placeholder="Lecture détaillée du dossier : contexte, dynamique du dirigeant, équilibres patrimoniaux, options envisagées, recommandations…"
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-4 text-[14.5px] leading-[1.85] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 resize-y"
                        />
                    </Block>
                </div>

                <div className="lg:col-span-5 space-y-10">
                    {/* Points d'attention */}
                    <Block
                        label="Points d'attention"
                        sub="3 à 5 points clés à conserver à l'esprit."
                        Icon={AlertTriangle}
                    >
                        <ul className="mb-3 space-y-2">
                            {(state.attention_points || []).map((p, idx) => (
                                <li
                                    key={idx}
                                    data-testid={`attention-${idx}`}
                                    className="group flex items-center justify-between gap-3 text-[13.5px] font-serif text-[#f4ecd8]/85 py-2 border-b border-white/[0.05]"
                                >
                                    <span>
                                        <span className="text-[#d4af37]/70 mr-2">·</span>
                                        {p}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removePoint(idx)}
                                        data-testid={`attention-delete-${idx}`}
                                        className="opacity-0 group-hover:opacity-100 text-[#f4ecd8]/30 hover:text-[#e89090] transition"
                                    >
                                        <X strokeWidth={1.3} size={13} />
                                    </button>
                                </li>
                            ))}
                            {(state.attention_points || []).length === 0 && (
                                <li className="text-[12px] text-[#f4ecd8]/35 italic font-light py-1">
                                    Aucun point identifié.
                                </li>
                            )}
                        </ul>
                        <div className="flex gap-2">
                            <input
                                data-testid="attention-input"
                                value={newPoint}
                                onChange={(e) => setNewPoint(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPoint())}
                                placeholder="Gouvernance, Confidentialité…"
                                className="flex-1 bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif text-[#f4ecd8]"
                            />
                            <button
                                type="button"
                                onClick={addPoint}
                                data-testid="attention-add"
                                className="text-[#d4af37]/80 hover:text-[#d4af37] transition"
                            >
                                <Plus strokeWidth={1.5} size={16} />
                            </button>
                        </div>
                    </Block>

                    {/* Écosystème mobilisé */}
                    <Block
                        label="Écosystème mobilisé"
                        sub="Expertises coordonnées sur le dossier."
                        Icon={Network}
                    >
                        <div className="flex flex-wrap gap-2">
                            {options.map((opt) => {
                                const active = (state.network || []).includes(opt);
                                return (
                                    <button
                                        key={opt}
                                        type="button"
                                        data-testid={`network-${opt}`}
                                        onClick={() => toggleNetwork(opt)}
                                        className={`text-[11px] tracking-[0.16em] uppercase px-3 py-1.5 border transition-all duration-500 ${
                                            active
                                                ? "border-[#d4af37] bg-[#d4af37]/15 text-[#d4af37]"
                                                : "border-white/15 text-[#f4ecd8]/55 hover:border-[#d4af37]/40 hover:text-[#f4ecd8]"
                                        }`}
                                    >
                                        {opt}
                                    </button>
                                );
                            })}
                        </div>
                    </Block>

                    {/* Décisions structurantes */}
                    <Block
                        label="Décisions structurantes"
                        sub="Mémoire des grandes orientations validées."
                        Icon={GitBranch}
                    >
                        <ul className="mb-3 space-y-2">
                            {(state.decisions || []).map((d) => (
                                <li
                                    key={d.id}
                                    data-testid={`decision-${d.id}`}
                                    className="group flex items-start justify-between gap-3 text-[13.5px] font-serif text-[#f4ecd8]/85 py-2 border-b border-white/[0.05]"
                                >
                                    <div className="flex-1 min-w-0">
                                        <span className="text-[#d4af37]/70 mr-2">▸</span>
                                        {d.label}
                                        {d.decided_at && (
                                            <span className="block text-[10px] text-[#f4ecd8]/35 italic mt-1 ml-5">
                                                {new Date(d.decided_at).toLocaleDateString("fr-FR", {
                                                    day: "2-digit",
                                                    month: "long",
                                                    year: "numeric",
                                                })}
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeDecision(d.id)}
                                        data-testid={`decision-delete-${d.id}`}
                                        className="opacity-0 group-hover:opacity-100 text-[#f4ecd8]/30 hover:text-[#e89090] transition mt-2"
                                    >
                                        <X strokeWidth={1.3} size={13} />
                                    </button>
                                </li>
                            ))}
                            {(state.decisions || []).length === 0 && (
                                <li className="text-[12px] text-[#f4ecd8]/35 italic font-light py-1">
                                    Aucune décision consignée.
                                </li>
                            )}
                        </ul>
                        <div className="flex gap-2">
                            <input
                                data-testid="decision-input"
                                value={newDecision}
                                onChange={(e) => setNewDecision(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDecision())}
                                placeholder="Développement Moyen-Orient retenu…"
                                className="flex-1 bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif text-[#f4ecd8]"
                            />
                            <button
                                type="button"
                                onClick={addDecision}
                                data-testid="decision-add"
                                className="text-[#d4af37]/80 hover:text-[#d4af37] transition"
                            >
                                <Plus strokeWidth={1.5} size={16} />
                            </button>
                        </div>
                    </Block>
                </div>
            </div>

            {letter && (
                <MissionLetterModal
                    letter={letter}
                    onClose={() => setLetter(null)}
                />
            )}
        </section>
    );
}

function Block({ label, sub, Icon, children }) {
    return (
        <div>
            <div className="overline text-[#d4af37]/70 mb-2.5">
                {Icon && (
                    <Icon
                        strokeWidth={1}
                        size={12}
                        className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                    />
                )}
                {label}
            </div>
            {sub && (
                <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light mb-4 leading-relaxed">
                    {sub}
                </p>
            )}
            {children}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// MissionLetterModal — preview, edit, PDF export
// ─────────────────────────────────────────────────────────────

function MissionLetterModal({ letter, onClose }) {
    const [body, setBody] = useState(letter.body || "");
    const [editing, setEditing] = useState(false);

    const print = () => {
        const w = window.open("", "_blank");
        if (!w) return;
        w.document.write(`
            <html>
            <head>
              <title>${letter.title}</title>
              <style>
                @page { margin: 28mm 24mm; }
                body {
                  font-family: "Cormorant Garamond", "Georgia", serif;
                  color: #1c1c1c;
                  font-size: 11.5pt;
                  line-height: 1.85;
                  max-width: 720px;
                  margin: 0 auto;
                  white-space: pre-line;
                }
                .head {
                  text-align: right;
                  font-family: "Helvetica Neue", sans-serif;
                  font-size: 8.5pt;
                  letter-spacing: 0.28em;
                  text-transform: uppercase;
                  color: #b89234;
                  margin-bottom: 28pt;
                  padding-bottom: 10pt;
                  border-bottom: 1px solid #d4af37;
                }
                .body { font-size: 11.5pt; }
                h1, h2, h3 { display: none; }
              </style>
            </head>
            <body>
              <div class="head">XV Capital Partners · Strategic Advisory · Côte d'Azur</div>
              <div class="body">${body
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")}</div>
              <script>window.onload = () => { setTimeout(() => window.print(), 350); };<\/script>
            </body>
            </html>
        `);
        w.document.close();
    };

    return (
        <div
            data-testid="mission-letter-modal"
            className="fixed inset-0 z-50 bg-[#050a13]/80 backdrop-blur-sm flex items-center justify-center p-4 lg:p-10"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-[#d4af37]/30 w-full max-w-[840px] max-h-[90vh] flex flex-col"
            >
                <div className="flex items-center justify-between px-7 py-5 border-b border-white/[0.08]">
                    <div>
                        <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70">
                            Lettre de mission · brouillon
                        </div>
                        <h3
                            className="font-serif text-[20px] text-[#f4ecd8] mt-1"
                            style={{ fontWeight: 400 }}
                        >
                            {letter.title}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setEditing((v) => !v)}
                            data-testid="letter-toggle-edit"
                            className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase px-3 py-2 text-[#f4ecd8]/65 hover:text-[#d4af37] transition"
                        >
                            <Pencil strokeWidth={1.3} size={12} />
                            {editing ? "Aperçu" : "Modifier"}
                        </button>
                        <button
                            type="button"
                            onClick={print}
                            data-testid="letter-export-pdf"
                            className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                        >
                            <Download strokeWidth={1.3} size={12} />
                            Exporter PDF
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-[#f4ecd8]/45 hover:text-[#f4ecd8] transition p-1"
                        >
                            <X strokeWidth={1.4} size={18} />
                        </button>
                    </div>
                </div>
                <div className="overflow-y-auto px-8 py-8">
                    {editing ? (
                        <textarea
                            data-testid="letter-body-edit"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            className="w-full min-h-[70vh] bg-transparent border border-white/10 focus:border-[#d4af37]/50 outline-none p-5 text-[14px] leading-[1.85] font-serif text-[#f4ecd8] resize-y whitespace-pre-line"
                        />
                    ) : (
                        <pre
                            data-testid="letter-body-preview"
                            className="font-serif text-[14.5px] leading-[1.95] text-[#f4ecd8]/90 whitespace-pre-wrap"
                            style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}
                        >
                            {body}
                        </pre>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// StrategicSummaryHeader — discreet block under dossier title
// ─────────────────────────────────────────────────────────────

export function StrategicSummaryHeader({ reading }) {
    if (!reading) return null;
    const hasContent =
        reading.mission_objective ||
        reading.main_challenge ||
        (reading.attention_points || []).length > 0;
    if (!hasContent) return null;

    return (
        <div
            data-testid="strategic-summary-header"
            className="mt-8 pt-7 border-t border-white/[0.05] grid md:grid-cols-3 gap-x-10 gap-y-6"
        >
            {reading.mission_objective && (
                <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70 mb-2">
                        Mission
                    </div>
                    <p className="text-[13.5px] font-serif text-[#f4ecd8]/85 leading-[1.7]">
                        {reading.mission_objective}
                    </p>
                </div>
            )}
            {reading.main_challenge && (
                <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70 mb-2">
                        Enjeu principal
                    </div>
                    <p className="text-[13.5px] font-serif text-[#f4ecd8]/85 leading-[1.7]">
                        {reading.main_challenge}
                    </p>
                </div>
            )}
            {(reading.attention_points || []).length > 0 && (
                <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70 mb-2">
                        Points d'attention
                    </div>
                    <ul className="space-y-1.5">
                        {reading.attention_points.slice(0, 5).map((p, i) => (
                            <li
                                key={i}
                                className="text-[13px] font-serif text-[#f4ecd8]/75 leading-snug"
                            >
                                <span className="text-[#d4af37]/70 mr-2">·</span>
                                {p}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// Trash2 imported but used to satisfy import linting if not used inline.
// eslint-disable-next-line no-unused-expressions
Trash2;
